import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/toaster';
import { useAuthStore } from '@/stores/authStore';
import { useSettingsStore } from '@/stores/settingsStore';

// Layouts
import MainLayout from '@/components/layouts/MainLayout';

// Auth Pages
import LoginPage from '@/features/auth/pages/LoginPage';
import RegisterPage from '@/features/auth/pages/RegisterPage';
import ForgotPasswordPage from '@/features/auth/pages/ForgotPasswordPage';

// Main Pages
import DashboardPage from '@/features/dashboard/pages/DashboardPage';
import AssetsPage from '@/features/assets/pages/AssetsPage';
import AssetDetailPage from '@/features/assets/pages/AssetDetailPage';
import AssetFormPage from '@/features/assets/pages/AssetFormPage';
import TicketsPage from '@/features/tickets/pages/TicketsPage';
import TicketDetailPage from '@/features/tickets/pages/TicketDetailPage';
import TicketFormPage from '@/features/tickets/pages/TicketFormPage';
import PublicSubmitTicketPage from '@/features/tickets/pages/PublicSubmitTicketPage';
import UsersPage from '@/features/users/pages/UsersPage';
import PermissionsPage from '@/features/permissions/pages/PermissionsPage';
import ImportWizardPage from '@/features/import/pages/ImportWizardPage';
import ReportsPage from '@/features/reports/pages/ReportsPage';
import SettingsPage from '@/features/settings/pages/SettingsPage';
import BackupManagementPage from '@/features/settings/pages/BackupManagementPage';
import MarkingPage from '@/features/marking/pages/MarkingPage';

// Management Pages
import BranchesPage from '@/features/branches/pages/BranchesPage';
import DepartmentsPage from '@/features/departments/pages/DepartmentsPage';
import LocationsPage from '@/features/locations/pages/LocationsPage';
import CategoriesPage from '@/features/categories/pages/CategoriesPage';
import BrandsPage from '@/features/brands/pages/BrandsPage';
import VendorsPage from '@/features/vendors/pages/VendorsPage';
import EmployeesPage from '@/features/employees/pages/EmployeesPage';
import EmployeeDetailPage from '@/features/employees/pages/EmployeeDetailPage';
import EmployeeFormPage from '@/features/employees/pages/EmployeeFormPage';
import EmployeeAssetsPage from '@/features/employees/pages/EmployeeAssetsPage';

// SLA Pages
import SLAPage from '@/features/sla/pages/SLAPage';

// Knowledge Base Pages
import KnowledgeBasePage from '@/features/knowledge-base/pages/KnowledgeBasePage';
import KBArticlePage from '@/features/knowledge-base/pages/KBArticlePage';
import KBArticleFormPage from '@/features/knowledge-base/pages/KBArticleFormPage';
import ConsumablesPage from '@/features/consumables/pages/ConsumablesPage';
import ConsumableFormPage from '@/features/consumables/pages/ConsumableFormPage';

// Profile, Organization, and Logs Pages
import ProfilePage from '@/features/profile/pages/ProfilePage';
import OrganizationPage from '@/features/organization/pages/OrganizationPage';
import AuditLogsPage from '@/features/audit-logs/pages/AuditLogsPage';
import ActivityLogsPage from '@/features/activity-logs/pages/ActivityLogsPage';
import { NotificationsPage } from '@/features/notifications';

// Sanalam Workflow Pages
import { ServiceProvidersPage, ServiceProviderDetailsPage } from '@/features/service-providers';
import { RepairsPage, RepairDetailsPage, EmployeeRepairHistoryPage } from '@/features/repairs';
import { AccessFormsPage, AccessFormDetailsPage } from '@/features/access-forms';
import { AssetIssueFormsPage, AssetIssueFormDetailsPage } from '@/features/asset-issue-forms';
import { DocumentsPage } from '@/features/documents';

// New Feature Pages
import LicensesPage from '@/features/licenses/pages/LicensesPage';
import PurchaseOrdersPage from '@/features/purchase-orders/pages/PurchaseOrdersPage';
import WarrantyAlertsPage from '@/features/warranty/pages/WarrantyAlertsPage';
import DepreciationPage from '@/features/depreciation/pages/DepreciationPage';
import ScannerPage from '@/features/scanner/pages/ScannerPage';
import PasswordVaultPage from '@/features/password-vault/pages/PasswordVaultPage';

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Public Route Component (redirects if authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full spinner" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

function App() {
  const { isAuthenticated } = useAuthStore();
  const { loadSettings } = useSettingsStore();

  // Load settings when user is authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadSettings();
    }
  }, [isAuthenticated, loadSettings]);

  return (
    <BrowserRouter>
      <Routes>
        {/* Auth Routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <RegisterPage />
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPasswordPage />
            </PublicRoute>
          }
        />

        {/* Public Submit Ticket Route - No authentication required */}
        <Route path="/submit-ticket" element={<PublicSubmitTicketPage />} />

        {/* Protected Routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="assets" element={<AssetsPage />} />
          <Route path="assets/new" element={<AssetFormPage />} />
          <Route path="assets/:id" element={<AssetDetailPage />} />
          <Route path="assets/:id/edit" element={<AssetFormPage />} />
          <Route path="tickets" element={<TicketsPage />} />
          <Route path="tickets/new" element={<TicketFormPage />} />
          <Route path="tickets/:id" element={<TicketDetailPage />} />
          <Route path="tickets/:id/edit" element={<TicketFormPage />} />
          <Route path="employees" element={<EmployeesPage />} />
          <Route path="employees/new" element={<EmployeeFormPage />} />
          <Route path="employees/:id" element={<EmployeeDetailPage />} />
          <Route path="employees/:id/assets" element={<EmployeeAssetsPage />} />
          <Route path="employees/:id/edit" element={<EmployeeFormPage />} />
          <Route path="consumables" element={<ConsumablesPage />} />
          <Route path="consumables/new" element={<ConsumableFormPage />} />
          <Route path="consumables/:id/edit" element={<ConsumableFormPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="permissions" element={<PermissionsPage />} />
          <Route path="import" element={<ImportWizardPage />} />
          <Route path="reports" element={<ReportsPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="backups" element={<BackupManagementPage />} />
          <Route path="marking" element={<MarkingPage />} />
          <Route path="branches" element={<BranchesPage />} />
          <Route path="departments" element={<DepartmentsPage />} />
          <Route path="locations" element={<LocationsPage />} />
          <Route path="categories" element={<CategoriesPage />} />
          <Route path="brands" element={<BrandsPage />} />
          <Route path="vendors" element={<VendorsPage />} />
          <Route path="sla-policies" element={<SLAPage />} />
          <Route path="knowledge-base" element={<KnowledgeBasePage />} />
          <Route path="knowledge-base/new" element={<KBArticleFormPage />} />
          <Route path="knowledge-base/:id" element={<KBArticlePage />} />
          <Route path="knowledge-base/:id/edit" element={<KBArticleFormPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="organization" element={<OrganizationPage />} />
          <Route path="audit-logs" element={<AuditLogsPage />} />
          <Route path="activity-logs" element={<ActivityLogsPage />} />
          
          {/* Sanalam Workflow Routes */}
          <Route path="service-providers" element={<ServiceProvidersPage />} />
          <Route path="service-providers/:id" element={<ServiceProviderDetailsPage />} />
          <Route path="repairs" element={<RepairsPage />} />
          <Route path="repairs/employee/:employeeId" element={<EmployeeRepairHistoryPage />} />
          <Route path="repairs/:id" element={<RepairDetailsPage />} />
          <Route path="access-forms" element={<AccessFormsPage />} />
          <Route path="access-forms/:id" element={<AccessFormDetailsPage />} />
          <Route path="asset-issue-forms" element={<AssetIssueFormsPage />} />
          <Route path="asset-issue-forms/:id" element={<AssetIssueFormDetailsPage />} />
          <Route path="documents" element={<DocumentsPage />} />
          
          {/* New Feature Routes */}
          <Route path="licenses" element={<LicensesPage />} />
          <Route path="purchase-orders" element={<PurchaseOrdersPage />} />
          <Route path="warranty-alerts" element={<WarrantyAlertsPage />} />
          <Route path="depreciation" element={<DepreciationPage />} />
          <Route path="scanner" element={<ScannerPage />} />
          <Route path="password-vault" element={<PasswordVaultPage />} />
        </Route>

        {/* 404 */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>

      <Toaster />
    </BrowserRouter>
  );
}

export default App;
